import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import moment from 'moment';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import { documentService } from '../../../services/central-api/document';
import { useQuery } from '@tanstack/react-query';
import CustomButton from '../../../components/CustomButton';
import DocumentModal from './DocumentModal';
import DropdownButton from '../../../components/DropdownButton';
import DocumentVersionModal from './DocumentVersionModal';
import Filter from './Filters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import useFilters from '../../../components/Filter/hooks/useFilters';

export default function Documents() {
    const route = useRoute<RouteProp<DocumentsParamList, 'DocumentsList'>>();
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();

    const [filters, setFilters] = useFilters<DocumentsParamList['DocumentsList']>({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');


    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>();
    const [isVersionModalVisible, setVersionModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getDocuments', filters],
        queryFn: () => documentService.getDocuments(filters),
        enabled: isFocused,
    });

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    function handleModalClose() {
        setSelectedDocumentId(undefined);
        setModalVisible(false);
        refetch();
    }

    return <Container>
        <Header
            title={translate('documents')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedDocumentId(undefined);
                setModalVisible(true);
            }} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredDocuments')}
                zIndex={2}
                filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={setTextFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('title'),
                            selector: row => row.title,
                            wrap: true,
                        },
                        {
                            name: translate('version'),
                            selector: row => row.versions[0].version,
                        },
                        {
                            name: translate('documentType'),
                            selector: row => row.documentCategory.type,
                            wrap: true,
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.documentCategory.actingBodyUnit.actingBody.name,
                            wrap: true,
                        },
                        {
                            name: translate('actingBodyUnit'),
                            selector: row => row.documentCategory.actingBodyUnit.name,
                            wrap: true,
                        },
                        {
                            name: translate('user'),
                            selector: row => row.user.warName || row.user.name,
                            wrap: true,
                        },
                        {
                            name: translate('createdAt'),
                            selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                            wrap: true,
                        },
                        {
                            cell: row => (
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedDocumentId(row.id);
                                                setModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('downloadDocument'),
                                            onPress: () => {
                                                setSelectedDocumentId(row.id);
                                                setVersionModalVisible(true);
                                            },
                                        }
                                    ]}
                                />
                            ),
                            wrap: true,
                            button: true,
                        }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        <DocumentModal
            onClose={handleModalClose}
            isModalVisible={isModalVisible}
            documentId={selectedDocumentId}
        />

        {isVersionModalVisible && selectedDocumentId && (
            <DocumentVersionModal
                onClose={() => {
                    setSelectedDocumentId(undefined);
                    setVersionModalVisible(false);
                }}
                isModalVisible={isVersionModalVisible}
                documentId={selectedDocumentId}
            />
        )}
    </Container>;
}
